import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { CustomSelect } from '@/components/form/CustomSelect';
import { SortableHeader } from '@/components/SortableHeader';
import { useColumnSort } from '@/lib/tableSort';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncData } from '@/hooks/useAsyncData';
import { fetchPortalUsers, fetchPortalUserFilters } from '@/api/portalUsers';

type SortKey = 'created_at' | 'firstName' | 'lastName' | 'client' | 'role' | 'status';

/**
 * "Пользователи ЛК" — 1:1 старый Orchid CustomerUserListScreen (см. докблок
 * PortalUserController в Laravel): таблица учётных записей пользователей
 * личных кабинетов.
 *
 * Один поиск сразу по имени, фамилии, компании и логину — в Orchid это были
 * отдельные фильтры на колонках, причём поиск по логину и по компании
 * исключали друг друга.
 */
export function PortalUsersPage() {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const { can } = usePermissions();
  // Ссылка на компанию ведёт в карточку клиента, а её открывает .view/.add/.edit
  // (одного .list мало — см. маршруты customers/{id} в Laravel).
  const canOpenClient = can(
    'platform*.customers.view',
    'platform*.customers.add',
    'platform*.customers.edit',
  );
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const sort = useColumnSort<SortKey>();
  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  useEffect(() => {
    setPage(1);
  }, [search, role, status]);

  // Справочники не критичны: таблица работает и без выпадающих списков.
  const { data: filters } = useAsyncData(fetchPortalUserFilters, []);
  const roles = filters?.roles ?? [];
  const statuses = filters?.statuses ?? [];

  const { data: list, error } = useAsyncData(
    () =>
      fetchPortalUsers({
        search: search || undefined,
        role: role || undefined,
        status: status || undefined,
        sort: sortParam,
        page,
      }),
    [search, role, status, sortParam, page],
    'Не удалось загрузить пользователей.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  return (
    <section className="catalog">
      <header className="catalog__header">
        <h1>Пользователи ЛК</h1>
        <p className="catalog__lede">
          Учётные записи пользователей личных кабинетов
          {meta ? ` · всего ${meta.total}` : ''}
        </p>
      </header>

      <div className="card">
        <div className="filter-row">
          {/* Короткий плейсхолдер — длинный перечень полей не влезал в поле
              и обрезался; что именно ищется, написано подсказкой ниже. */}
          <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск" />
          <div className="group-select">
            <CustomSelect
              value={role}
              options={[{ value: '', label: 'Все роли' }, ...roles]}
              onChange={setRole}
            />
          </div>
          <div className="group-select group-select--wide">
            <CustomSelect
              value={status}
              options={[{ value: '', label: 'Все статусы' }, ...statuses]}
              onChange={setStatus}
            />
          </div>
        </div>
        <p className="edit-field__hint filter-hint">
          Поиск по имени, фамилии, названию компании или логину.
        </p>

        {error && <p className="field-error">{error}</p>}

        {rows === null ? (
          <div className="panel-loading">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <p className="cell-muted">Пользователи не найдены.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="Имя" sortKey="firstName" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Фамилия" sortKey="lastName" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Компания" sortKey="client" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Роль" sortKey="role" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Статус" sortKey="status" sort={sort} onSort={sort.toggle} />
                  <th>Логин</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  // Строки некликабельны: таблица только для ознакомления,
                  // своей страницы у пользователя ЛК нет.
                  <tr key={row.id}>
                    <td>{row.firstName ?? '—'}</td>
                    <td>{row.lastName ?? '—'}</td>
                    {/* Строка целиком некликабельна (таблица для
                        ознакомления), но с компании можно перейти в её
                        карточку — это единственный осмысленный переход. */}
                    <td className="cell-primary">
                      {canOpenClient && row.clientId && row.client ? (
                        <Link to={`/clients/${row.clientId}`}>{row.client}</Link>
                      ) : (
                        (row.client ?? '—')
                      )}
                    </td>
                    <td>{row.roleName ?? '—'}</td>
                    <td>
                      <span className={`badge ${row.status === 'active' ? 'badge--good' : 'badge--warn'}`}>
                        {statuses.find((s) => s.value === row.status)?.label ?? row.status ?? '—'}
                      </span>
                    </td>
                    <td className="cell-muted">{row.login ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.lastPage > 1 && (
          <div className="table-footer">
            <span className="table-footer__status">
              Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
            </span>
            <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
          </div>
        )}
      </div>
    </section>
  );
}
